import moment from 'moment';
import {
  CLIENT_DETAILS,
  DOCTORS_ROUTE,
  MEDICARE_AND_HEALTH,
  REVIEW,
  SPECIALITIES_ROUTE,
} from '../constants/routes';
import { AppointmentData } from '../model';
import { DATE_WITH_DASH, HOUR } from '../constants/time-formats';

export const BOOKING_STEPS = ['Appointment', 'Your details', 'Medicare & health', 'Review & consent'];

const STEP_BY_PATH: Record<string, number> = {
  [SPECIALITIES_ROUTE]: 0,
  [DOCTORS_ROUTE]: 0,
  [CLIENT_DETAILS]: 1,
  [MEDICARE_AND_HEALTH]: 2,
  [REVIEW]: 3,
};

const trimTrailingSlash = (pathname: string) => (pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname);

// Index into BOOKING_STEPS, or undefined for pages outside the four steps (e.g. /thanks).
export const getBookingStep = (pathname: string): number | undefined => STEP_BY_PATH[trimTrailingSlash(pathname)];

// A clinician's own booking link carries both ids, e.g. /client-details?specialist=<id>&specialty=<id>.
export const isDirectClinicianLink = (search: string) => {
  const params = new URLSearchParams(search);
  return Boolean(params.get('specialist') && params.get('specialty'));
};

export const getAppointmentEntryRoute = (search: string) => (isDirectClinicianLink(search) ? DOCTORS_ROUTE : SPECIALITIES_ROUTE);

export const hasSelectedAppointment = (appointment?: Partial<AppointmentData>) => Boolean(
  appointment?.specialistId && appointment?.date && appointment?.startTime,
);

type RedirectInput = {
  pathname: string;
  search: string;
  hasAppointment: boolean;
  hasClient: boolean;
};

// Appointment selection comes first: no personal details until a time is chosen,
// and nothing after "Your details" until those details exist.
export const getBookingRedirect = ({
  pathname, search, hasAppointment, hasClient,
}: RedirectInput): string | null => {
  const path = trimTrailingSlash(pathname);
  if (path === '' || path === '/') {
    return `${getAppointmentEntryRoute(search)}${search}`;
  }

  const step = getBookingStep(path);
  if (step === undefined) {
    return null;
  }
  if (step >= 1 && !hasAppointment) {
    return `${getAppointmentEntryRoute(search)}${search}`;
  }
  if (step >= 2 && !hasClient) {
    return `${CLIENT_DETAILS}${search}`;
  }
  return null;
};

// e.g. "Thursday 24 Sep at 9:15 AM"; null until both a date and a time are chosen.
export const formatAppointmentWhen = (appointment?: Partial<AppointmentData>): string | null => {
  if (!appointment?.date || !appointment?.startTime) {
    return null;
  }
  return moment(`${appointment.date} ${appointment.startTime}`, `${DATE_WITH_DASH} ${HOUR}`).format('dddd D MMM [at] h:mm A');
};
